import React, { useState, ChangeEvent } from 'react';
import Button from '../../../../components/UI/Button';
import CustomInputField from '../../../../components/UI/CustomInputField';
import Message from '../../../../components/UI/Message';
import RadioButtonGroup from '../../../../components/UI/RadioButtonGroup';

import { useApp } from '../../../../state/app';
import { useUser } from '../../../../state/user';

import constants from '../constants';
import { FundingAccountState } from '../shared';

import { BaseAchProps } from './shared';

import css from '../FundingAccount.module.scss';

const { manualACHMaxError } = constants;

interface AchFormProps extends BaseAchProps {
  error: boolean;
  invalidAmountError: boolean;
  saveAchAccount: (state: FundingAccountState) => void;
  getBankNameDispatch: (routing: string) => void;
  isMorganStanleyFlow?: boolean;
}

export default function ACHForm({
  error,
  invalidAmountError,
  fundingSource,
  saveAchAccount,
  getBankNameDispatch,
  isMorganStanleyFlow
}: AchFormProps) {
  const { loading } = useApp();
  const { bankName } = useUser();

  const [formState, updateFormState] = useState<FundingAccountState>({
    routingNumber: '',
    achName: '',
    radioSelected: null,
    accountNumber: '',
    validateAccountNumber: ''
  });

  const setFormState = (val: any) => {
    updateFormState((state: FundingAccountState) => ({ ...state, ...val }));
  };

  const onInputChange = (which: string) => (value: any) => {
    if (which === 'routingNumber' && value.length > 8) {
      getBankNameDispatch(value);
    }
    if (which === 'routingNumber' && !/^\d*$/.test(value)) {
      return;
    }
    setFormState({ [which]: value });
  };

  const onRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormState({ radioSelected: e.target.value });
  };

  const getErrorMessage = () => {
    if (formState.accountNumber !== formState.validateAccountNumber) {
      return 'Account numbers entered are not equal.';
    }

    if (invalidAmountError) {
      return manualACHMaxError;
    }
  };

  const {
    routingNumber,
    achName,
    radioSelected,
    accountNumber,
    validateAccountNumber
  } = formState;

  return (
    <div
      className={`${css.AddFundingAccountForm} ${
        isMorganStanleyFlow ? css.noTopSpacing : ''
      }`}
    >
      {(invalidAmountError || error) && getErrorMessage() && (
        <Message
          data-testid="ach-error-msg"
          error={true}
          text={getErrorMessage()}
        />
      )}
      <div>
        <p className={css.accountSelectLabel}>Account Type</p>
        <RadioButtonGroup
          radioButtons={[
            { value: 'checking', label: 'Checking' },
            { value: 'savings', label: 'Savings' }
          ]}
          selected={radioSelected as string}
          onChange={onRadioChange}
        />
      </div>
      <CustomInputField
        id="name"
        placeholder="Name on account"
        autoComplete="off"
        autoCorrect="off"
        type="text"
        error={error && !achName}
        value={achName}
        onChange={onInputChange('achName')}
      />
      <CustomInputField
        id="routing-number"
        placeholder="Routing number"
        autoComplete="off"
        autoCorrect="off"
        maxLength={9}
        error={error && !routingNumber}
        value={routingNumber}
        onChange={onInputChange('routingNumber')}
        inputMode="tel"
        type="numeric"
        pattern="[0-9]*"
        format={bankName || ''}
        className="fs-mask"
      />
      <div>
        <CustomInputField
          id="account-number"
          placeholder="Account number"
          autoComplete="off"
          autoCorrect="off"
          error={
            error && (!accountNumber || accountNumber !== validateAccountNumber)
          }
          value={accountNumber}
          onChange={onInputChange('accountNumber')}
          inputMode="tel"
          type="numeric"
          pattern="[0-9]*"
          className="fs-mask"
        />
        <CustomInputField
          id="validate-account-number"
          placeholder="Re-enter account number"
          autoComplete="off"
          autoCorrect="off"
          error={
            error &&
            (!validateAccountNumber || accountNumber !== validateAccountNumber)
          }
          value={validateAccountNumber}
          onChange={onInputChange('validateAccountNumber')}
          inputMode="tel"
          type="numeric"
          pattern="[0-9]*"
          className="fs-mask"
        />
      </div>

      <div className={css.AddFundingAccountCheckImg}>
        <img src="../../../../static/check-2-x@2x.png" alt="check" />
      </div>
      <Button
        text="Next"
        onClick={() => saveAchAccount(formState)}
        disabled={invalidAmountError && fundingSource === 'ach'}
        loading={loading}
      />
      <p>
        By clicking Next, I authorize Greenlight to debit funds from my bank
        account for the amounts I select. See your{' '}
        <a
          href={`https://greenlight.com/cardholder`}
          target="_blank"
          rel="noreferrer"
        >
          Cardholder Agreement
        </a>{' '}
        for more details.
      </p>
    </div>
  );
}
